import { useRef, useState, useCallback } from "react";

interface UseDebouncedInputOptions<T> {
  initialValue: T;
  onChange: (value: T) => void;
  debounceMs?: number;
  transform?: (input: string) => T;
}

interface UseDebouncedInputReturn {
  localValue: string;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  setLocalValue: (value: string) => void;
}

export function useDebouncedInput<T>(
  options: UseDebouncedInputOptions<T>,
): UseDebouncedInputReturn {
  const debounceMs = options.debounceMs ?? 500;
  const [localValue, setLocalValue] = useState(
    options.initialValue?.toString() ?? "",
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const val = e.target.value;
      setLocalValue(val);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (options.transform) {
          options.onChange(options.transform(val));
        } else {
          options.onChange(val as T);
        }
      }, debounceMs);
    },
    [options.onChange, options.transform, debounceMs],
  );

  return {
    localValue,
    handleChange,
    setLocalValue,
  };
}

export function parseNullableNumber(val: string): number | null {
  if (val === "") {
    return null;
  }

  const numVal = parseInt(val, 10);
  return isNaN(numVal) ? null : numVal;
}

export function parseNullableString(val: string): string | null {
  return val || null;
}
